import { getLiquidEngine } from '../services/liquid.service';
import productPriceTemplate from '../../snippets/product-price-v2.liquid';
import '../../styles/web-components/cart-line-item.scss';
import { emitShopifyEvent } from '../services/analytics.service';
import { showSimpleMessageModal } from '../services/dialog.service';
import { removeSaveForLaterProducts, saveProductsForLater } from '../services/wishlist.service';
import { addToCart, changeLineItem, ChangeLineItemError } from '../services/cart.service';
class CartLineItem extends HTMLElement {
    static get observedAttributes() {
        return ["cart-line-item-data", "line-number", "is-available"];
    }
    lineItemData;
    lineNumber;
    isAvailable = true; // assume line item is available unless told otherwise
    constructor() {
        super();
        this.addEventListener('click', async (e) => {
            const target = e.target;
            if (target.classList.contains('js-qty-adjust')) {
                const val = target.classList.contains('icon-plus') ? 1 : -1;
                this.updateQuantity(this.lineItemData.quantity + val);
            }
            else if (target.classList.contains('js-remove-from-cart')) {
                this.showSpinner();
                this.removeLineItem();
            }
            else if (target.classList.contains('js-save-for-later')) {
                this.showSpinner();
                const payload = [{
                        epi: this.lineItemData.variant_id,
                        empi: this.lineItemData.product_id,
                        du: location.origin + this.lineItemData.url
                    }];
                saveProductsForLater(payload).then(() => {
                    this.removeLineItem();
                }).catch(() => {
                    showSimpleMessageModal("There was an error saving this product for later. Please try again later.");
                });
            }
            else if (target.classList.contains('js-remove-from-sfl')) {
                this.showSpinner();
                removeSaveForLaterProducts([{
                        epi: this.lineItemData.variant_id,
                        empi: this.lineItemData.product_id,
                        du: this.lineItemData.url
                    }]);
            }
            else if (target.classList.contains('js-move-to-cart')) {
                this.showSpinner();
                try {
                    await addToCart({ items: [{ id: this.lineItemData.variant_id, quantity: 1 }] });
                    removeSaveForLaterProducts([{
                            epi: this.lineItemData.variant_id,
                            empi: this.lineItemData.product_id,
                            du: this.lineItemData.url
                        }]);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                }
                catch (err) {
                    //
                }
            }
        });
        this.addEventListener('change', (e) => {
            const target = e.target;
            if (target.classList.contains('js-qty') && target.validity.valid) {
                const newQuantity = parseInt(target.value) || 0;
                this.updateQuantity(newQuantity);
            }
        });
    }
    connectedCallback() {
        this.append(this.generateHtml(this.lineItemData));
    }
    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue)
            return;
        switch (name) {
            case 'cart-line-item-data':
                this.lineItemData = JSON.parse(newValue);
                break;
            case 'line-number':
                this.lineNumber = parseInt(newValue);
                break;
            case 'is-available':
                this.isAvailable = newValue === "true";
                break;
            default:
                break;
        }
    }
    updateQuantity(newQuantity) {
        this.showSpinner();
        if (newQuantity < 1) {
            this.removeLineItem();
        }
        else {
            changeLineItem({ id: this.lineItemData.key, quantity: newQuantity }).catch((rejection) => {
                let message = window.cartUI.errors.update.generic.replace("#{product}", this.lineItemData.product_title);
                if (rejection instanceof ChangeLineItemError) {
                    if (rejection.payload?.reason === 'SOLD_OUT') {
                        message = window.cartUI.errors.update.soldOut.replace("#{product}", this.lineItemData.product_title);
                    }
                    if (rejection.payload?.passedthroughServerMessage) {
                        message = rejection.payload.passedthroughServerMessage;
                    }
                }
                showSimpleMessageModal(message);
            });
        }
    }
    removeLineItem() {
        changeLineItem({ id: this.lineItemData.key, quantity: 0 }).then(() => {
            emitShopifyEvent("remove_from_cart", JSON.parse(JSON.stringify(this.lineItemData)));
            const container = this.querySelector('.cart-line-item__container');
            container.addEventListener('animationend', () => {
                this.remove();
            }, { once: true });
            container.classList.add('fade-out');
        }).catch(() => {
            const message = window.cartUI.errors.remove.generic.replace("#{product}", this.lineItemData.product_title);
            showSimpleMessageModal(message);
        });
    }
    generateHtml(data) {
        const template = document.createElement('template');
        template.innerHTML = `
      <div class="cart-line-item__container">
        <a class="cart-line-item__image-container" href="${data.url}" title="${data.product_title}">
          <img class="cart-line-item__image" src="${data.featured_image.url}&width=300" alt="${data.featured_image.alt || data.product_title}" loading="lazy" height="136" width="115">
        </a>
        <div class="cart-line-item__info-container">
          <a class="cart-line-item__product-title" href="${data.url}" title="${data.variant_title}">${data.product_title}</a>
          <ul class="cart-line-item__product-details">
            ${this.getProductDetails(data).map(o => `<li>${o}</li>`).join("")}
          </ul>
          ${this.getAttribute("show-quantity") === "false" || !this.isAvailable ? '' : `
            <div class="cart-line-item__quantity-container">
              <button type="button" class="js-qty-adjust icon-minus" aria-label="Remove 1 ${data.product_title}"></button>
              <label>
                <span class="ADA__visually-hidden">Quantity For ${data.product_title} ${data.variant_title}</span>
                <input type="number" class="js-qty" value="${data.quantity}" pattern="\\d*" max="99" min="0" />
              </label>
              <button type="button" class="js-qty-adjust icon-plus" aria-label="Add 1 ${data.product_title}"></button>
            </div>
          `}
          <div class="cart-line-item__price">
            ${buildLineItemPrice(data.final_price, data.original_price, +this.getAttribute("compare-at-price"), this.getAttribute("badge-text"), this.isAvailable, ["inline-cart", "add-to-cart-overlay"].includes(this.getAttribute("actions-type")) ? null : data.product_id)}
          </div>
          ${this.getAttribute("actions-type") === "inline-cart" ?
            `<div class="cart-line-item__actions">
              <button type="button" class="cart-line-item__action js-remove-from-cart">Remove</button>
              ${window.UNTUCKitConfig.thirdPartyToggles.swym && window.UNTUCKitConfig.thirdPartyToggles.swymSaveForLater && this.isAvailable ?
                `<button type="button" class="cart-line-item__action js-save-for-later">Save For Later</button>` : ""}
            </div>` : ""}
          ${this.getAttribute("actions-type") === "sfl" && window.UNTUCKitConfig.thirdPartyToggles.swym && window.UNTUCKitConfig.thirdPartyToggles.swymSaveForLater ?
            `<div class="cart-line-item__actions">
              <button type="button" class="cart-line-item__action js-remove-from-sfl">Remove</button>
              ${this.isAvailable ? `
                <button type="button" class="cart-line-item__action js-move-to-cart">Move To Cart</button>
              ` : ''}
            </div>` : ""}
        </div>
        <loading-spinner show="false" blocking="true"></loading-spinner>
      </div>`;
        return template.content.firstElementChild;
    }
    getProductDetails(data) {
        const displayProperties = Object.entries(data.properties || {}).filter(e => !e[0].startsWith('_'))
            .map(e => `${e[0]}: ${e[1]}`);
        if (displayProperties.length) {
            return displayProperties;
        }
        else {
            return data.options_with_values.map(v => {
                const { name, value } = v;
                if (name.toLowerCase() === 'color') {
                    return this.getAttribute('color-display-value') || value;
                }
                else {
                    return value;
                }
            });
        }
    }
    showSpinner() {
        const loadingSpinner = this.querySelector('loading-spinner');
        loadingSpinner.setAttribute('show', 'true');
    }
}
if (!window.customElements.get("cart-line-item")) {
    customElements.define("cart-line-item", CartLineItem);
}
function buildLineItemPrice(currentPrice, totalPrice, compareAtPrice, subMessage, isAvailable, productId) {
    const regularPrice = compareAtPrice || totalPrice;
    if (!isAvailable) {
        subMessage = `Out of Stock`;
    }
    return getLiquidEngine().parseAndRenderSync(productPriceTemplate, { product: { id: productId }, regular_price: regularPrice, sale_price: currentPrice, is_small_version: true, sub_message: subMessage, mode: 'compact', show_percentage_saved: false, show_price_disclaimer: false });
}
